/**
 * Persistent suppression for lead-magnet popups and booking CTAs.
 *
 * Signals tracked (all keyed in localStorage so they survive across sessions):
 *   - resources_downloaded: which lead magnets the visitor has already grabbed
 *   - sessions_booked:      which session types the visitor has already booked
 *
 * Each signal is written immediately on a successful form submit AND can be
 * hydrated from the backend (cross-device) via:
 *   - GET /api/me/lead-suppression   (logged-in visitors, cookie-based)
 *   - GET /api/lead-suppression/lookup?email=...  (anonymous, by email)
 *
 * Best-effort throughout: localStorage failures (private mode / quota) are
 * silently ignored.  Backend failures fall back to localStorage-only.
 */

import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const KEY = 'dca_lead_magnet_downloads';
const SESSION_KEY = 'dca_sessions_booked';

// ---------- low-level localStorage helpers ----------

function _read(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === 'object') ? parsed : {};
  } catch (e) { return {}; }
}

function _write(key, obj) {
  try { localStorage.setItem(key, JSON.stringify(obj)); } catch (e) { /* ignore */ }
}

// ---------- resource downloads ----------

export function markResourceDownloaded(resource_id, email = '') {
  if (!resource_id) return;
  const all = _read(KEY);
  all[resource_id] = {
    email: (email || '').toLowerCase().trim() || null,
    ts: new Date().toISOString(),
  };
  _write(KEY, all);
}

export function hasDownloadedResource(resource_id) {
  const all = _read(KEY);
  if (!resource_id) return Object.keys(all).length > 0;
  return Boolean(all[resource_id]);
}

// ---------- session bookings ----------

export function markSessionBooked(session_type, email = '') {
  if (!session_type) return;
  const all = _read(SESSION_KEY);
  all[session_type] = {
    email: (email || '').toLowerCase().trim() || null,
    ts: new Date().toISOString(),
  };
  _write(SESSION_KEY, all);
}

export function hasBookedSession(session_type) {
  const all = _read(SESSION_KEY);
  if (!session_type) return Object.keys(all).length > 0;
  return Boolean(all[session_type]);
}

// ---------- backend hydration ----------

/**
 * Pull suppression signals from the server and merge into localStorage.
 * Source: `/api/me/lead-suppression` (uses session cookie - anonymous-safe).
 * Call once on app mount.
 */
export async function hydrateSuppressionFromBackend() {
  try {
    const res = await axios.get(`${API_URL}/api/me/lead-suppression`, {
      withCredentials: true,
      timeout: 5000,
    });
    const { resources_downloaded = [], sessions_booked = [] } = res.data || {};
    resources_downloaded.forEach((rid) => markResourceDownloaded(rid, ''));
    sessions_booked.forEach((st) => markSessionBooked(st, ''));
  } catch (e) {
    /* best-effort - keep whatever localStorage has */
  }
}

/**
 * Look up suppression signals by email (anonymous - no session needed).
 * Used the moment a visitor types their email into a form, so we can
 * suppress the popup mid-session if they've already converted on another
 * device.
 */
export async function lookupSuppressionByEmail(email) {
  const e = (email || '').toLowerCase().trim();
  if (!e || !e.includes('@')) return;
  try {
    const res = await axios.get(`${API_URL}/api/lead-suppression/lookup`, {
      params: { email: e },
      timeout: 5000,
    });
    const { resources_downloaded = [], sessions_booked = [] } = res.data || {};
    resources_downloaded.forEach((rid) => markResourceDownloaded(rid, e));
    sessions_booked.forEach((st) => markSessionBooked(st, e));
  } catch (err) {
    /* best-effort */
  }
}

/** Admin/debug helper: wipe all suppression flags from this browser. */
export function clearAllSuppressionFlags() {
  try { localStorage.removeItem(KEY); } catch (e) { /* ignore */ }
  try { localStorage.removeItem(SESSION_KEY); } catch (e) { /* ignore */ }
}
